import React, { useCallback, useState } from 'react';
import type { ConfigFieldDto } from '@api/nodeDefinitions';
import { PeoplePicker, type PeoplePickerPerson } from '@aibuild/ui';
import SelectField from './SelectField';
import MultiSelectField from './MultiSelectField';
import NodeFieldLabel from './NodeFieldLabel';
import { RequiredCheckbox, TextArea, TextInput } from './nodeCommon.styled';

/**
 * Buffers a controlled text value in local state so that React preserves
 * cursor position during typing. The parent's prop is only adopted when
 * it changes to something we didn't report (i.e. an external change such
 * as undo or a websocket push, not our own echo bouncing back).
 */
function useBufferedText(
  externalValue: string,
  onCommit: (next: string) => void,
) {
  const [buf, setBuf] = useState({
    local: externalValue,
    prevExternal: externalValue,
    lastCommitted: externalValue,
  });

  let { local } = buf;
  if (externalValue !== buf.prevExternal) {
    const isExternalChange = externalValue !== buf.lastCommitted;
    local = isExternalChange ? externalValue : buf.local;
    setBuf({
      local,
      prevExternal: externalValue,
      lastCommitted: isExternalChange ? externalValue : buf.lastCommitted,
    });
  }

  const handleChange = useCallback(
    (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >,
    ) => {
      const v = e.target.value;
      setBuf((prev) => ({ ...prev, local: v, lastCommitted: v }));
      onCommit(v);
    },
    [onCommit],
  );

  return [local, handleChange] as const;
}

export interface ConfigFieldRendererProps {
  readonly field: ConfigFieldDto;
  readonly value: unknown;
  readonly onChange: (next: unknown) => void;
  readonly nodeId: string;
  readonly readOnly: boolean;
  /** People available for PEOPLE_PICKER fields. */
  readonly people?: readonly PeoplePickerPerson[];
  /** Active org groups available for GROUP_PICKER fields. */
  readonly groups?: readonly { id: string; name: string }[];
  /**
   * Whether the org groups query has resolved. Until it has, `groups` is
   * `[]` for "not loaded yet" — distinct from "loaded, none exist" — so the
   * GROUP_PICKER must not show a persisted selection as `(deleted group)`.
   */
  readonly groupsLoaded?: boolean;
  /**
   * The full live config blob the parent passes through unchanged. Used
   * to evaluate this field's `visibleWhen` predicate (when present) by
   * looking up sibling field values. Optional — fields without a
   * predicate render regardless.
   */
  readonly siblingValues?: Readonly<Record<string, unknown>>;
}

/**
 * True when {@code field.visibleWhen} is empty / absent OR every entry
 * in it equals the corresponding sibling value (string-coerced so the
 * backend's enum-name strings match the wire-format values stored in
 * the data blob).
 */
function isFieldVisible(
  field: ConfigFieldDto,
  siblingValues: Readonly<Record<string, unknown>> | undefined,
): boolean {
  const predicate = field.visibleWhen;
  if (!predicate || Object.keys(predicate).length === 0) return true;
  if (!siblingValues) return false;
  for (const [key, expected] of Object.entries(predicate)) {
    const actual = siblingValues[key];
    if (actual == null) return false;
    if (String(actual) !== expected) return false;
  }
  return true;
}

const ConfigFieldRenderer: React.FC<ConfigFieldRendererProps> = ({
  field,
  value,
  onChange,
  nodeId,
  readOnly,
  people = [],
  groups = [],
  groupsLoaded = false,
  siblingValues,
}) => {
  const testId = `process-node__${field.key}-${nodeId}`;
  const hintTestId = `${testId}__hint`;
  const stringValue = value == null ? '' : String(value);

  const onCommitString = useCallback(
    (v: string) => {
      if (!readOnly) onChange(v);
    },
    [readOnly, onChange],
  );

  const [bufferedValue, handleBufferedChange] = useBufferedText(
    stringValue,
    onCommitString,
  );

  const handleBool = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (readOnly) return;
      onChange(e.target.checked);
    },
    [readOnly, onChange],
  );
  const handleNumber = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (readOnly) return;
      const raw = e.target.value;
      if (raw === '') {
        onChange(null);
        return;
      }
      // Negatives are meaningless for every editor number (credits,
      // hours, iterations) — reject them so a typed "-" can't commit
      // what the input's min already blocks on the spinners.
      const parsed = Number(raw);
      if (!Number.isNaN(parsed) && parsed >= 0) onChange(parsed);
    },
    [readOnly, onChange],
  );

  // Visibility predicate runs AFTER hooks so the hook order stays stable
  // when a field flips visible/hidden between renders (rules-of-hooks).
  if (!isFieldVisible(field, siblingValues)) return null;

  switch (field.type) {
    case 'TEXT':
      return (
        <div>
          <NodeFieldLabel label={field.label} hint={field.helpText} dataTestId={hintTestId} />
          <TextInput
            value={bufferedValue}
            onChange={handleBufferedChange}
            disabled={readOnly}
            placeholder={field.placeholder ?? undefined}
            aria-label={field.label}
            data-testid={testId}
          />
        </div>
      );
    case 'TEXTAREA':
      return (
        <div>
          <NodeFieldLabel label={field.label} hint={field.helpText} dataTestId={hintTestId} />
          <TextArea
            rows={field.rows ?? 3}
            value={bufferedValue}
            onChange={handleBufferedChange}
            disabled={readOnly}
            placeholder={field.placeholder ?? undefined}
            aria-label={field.label}
            data-testid={testId}
          />
        </div>
      );
    case 'SELECT':
      return (
        <SelectField
          label={field.label}
          hint={field.helpText}
          hintTestId={hintTestId}
          options={field.options}
          value={stringValue}
          onChange={onChange}
          readOnly={readOnly}
          placeholder={field.placeholder ?? undefined}
          testId={testId}
        />
      );
    case 'MULTI_SELECT':
      return (
        <MultiSelectField
          label={field.label}
          hint={field.helpText}
          hintTestId={hintTestId}
          options={field.options}
          value={Array.isArray(value) ? (value as string[]) : []}
          onChange={onChange}
          readOnly={readOnly}
          placeholder={field.placeholder ?? undefined}
          testId={testId}
          maxSelected={field.maxSelected}
        />
      );
    case 'CHECKBOX':
      return (
        <RequiredCheckbox>
          <input
            type="checkbox"
            checked={value === true}
            onChange={handleBool}
            disabled={readOnly}
            aria-label={field.label}
            data-testid={testId}
          />
          <span>{field.label}</span>
        </RequiredCheckbox>
      );
    case 'NUMBER':
      return (
        <div>
          <NodeFieldLabel label={field.label} hint={field.helpText} dataTestId={hintTestId} />
          <TextInput
            type="number"
            min={0}
            value={stringValue}
            onChange={handleNumber}
            disabled={readOnly}
            placeholder={field.placeholder ?? undefined}
            aria-label={field.label}
            data-testid={testId}
          />
        </div>
      );
    case 'PEOPLE_PICKER':
      return (
        <div>
          <NodeFieldLabel label={field.label} hint={field.helpText} dataTestId={hintTestId} />
          <PeoplePicker
            people={people}
            selectedIds={Array.isArray(value) ? (value as string[]) : []}
            onChange={onChange}
            disabled={readOnly}
            placeholder="Select reviewers…"
            emptySelectionLabel="Everyone in your organization"
            dataTestId={testId}
          />
        </div>
      );
    case 'GROUP_PICKER': {
      const knownIds = new Set(groups.map((g) => g.id));
      const options = groups.map((g) => ({ value: g.id, label: g.name }));
      // Surface a stale selection (group since deleted) so the author sees
      // why the gate is flagged rather than a bare id. Only once groups have
      // loaded — otherwise a valid id flashes as deleted before the query
      // resolves.
      if (groupsLoaded && stringValue && !knownIds.has(stringValue)) {
        options.unshift({ value: stringValue, label: '(deleted group)' });
      }
      return (
        <div>
          <NodeFieldLabel label={field.label} hint={field.helpText} dataTestId={hintTestId} />
          <SelectField
            options={options}
            value={stringValue}
            onChange={onChange}
            readOnly={readOnly}
            placeholder="Select a group…"
            ariaLabel={field.label}
            testId={testId}
            searchable
            searchPlaceholder="Search groups…"
          />
        </div>
      );
    }
    default:
      return null;
  }
};

export default ConfigFieldRenderer;
